/**
 * Perfil de operação do tenant (spec 017, D2/FR-004): o vocabulário de um
 * vertical é dado — preset + termos sobrescritos + recurso + cor + layout
 * padrão. Vale para TODAS as telas do tenant: equipe, portal e páginas
 * públicas. Troca de preset não apaga termo sobrescrito, e a tela mostra o que
 * muda antes de salvar (edge case da spec).
 */
import { useMemo, useState } from 'react';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import {
  PROFILE_PRESETS,
  PROFILE_PRESET_LABELS,
  RESOURCE_SOURCES,
  RESOURCE_SOURCE_LABELS,
  TERM_KEYS,
  UI_LAYOUTS,
  UI_LAYOUT_LABELS,
  resolveTerms,
  type OrgProfile,
  type OrgProfilePutBody,
  type ProfilePreset,
  type ResourceSource,
  type TenantProfile,
  type TermKey,
  type UiLayout,
} from '@erp/shared';
import { api, type ApiError } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input, Label, Select } from '@/components/ui/input';
import { PageSpinner } from '@/components/ui/misc';
import { SIMPLIFIED_LAYOUT_AS_TENANT_DEFAULT } from '@/shared/ui-prefs';

const ORG_PROFILE_KEY = ['erp', 'org', 'profile'] as const;

function orgProfileQuery() {
  return queryOptions({
    queryKey: ORG_PROFILE_KEY,
    queryFn: async ({ signal }) => {
      const res = await api<OrgProfile>('/org/profile', { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

/** O que cada termo nomeia — em palavras neutras, para qualquer vertical. */
const TERM_LABELS: Record<TermKey, string> = {
  ticket: 'Atendimento',
  tickets: 'Atendimentos (plural)',
  client: 'Cliente (quem contrata)',
  clients: 'Clientes (plural)',
  contact: 'Contato (quem é atendido)',
  contacts: 'Contatos (plural)',
  resource: 'Recurso',
  resources: 'Recursos (plural)',
  resourceId: 'Identificador do recurso',
  desk: 'Mesa',
  desks: 'Mesas (plural)',
  stage: 'Etapa',
  stages: 'Etapas (plural)',
  'situation.new': 'Situação: novo',
  'situation.in_progress': 'Situação: em andamento',
  'situation.waiting_client': 'Situação: aguardando cliente',
  'situation.waiting_contact': 'Situação: aguardando contato',
  'situation.waiting_third_party': 'Situação: aguardando terceiro',
  'situation.done': 'Situação: concluído',
  'situation.canceled': 'Situação: cancelado',
  'nature.installation': 'Natureza: instalação',
  'nature.maintenance': 'Natureza: manutenção',
  'nature.removal': 'Natureza: retirada',
  'nature.support': 'Natureza: suporte',
  'nature.other': 'Natureza: outro',
  'nature.internal': 'Natureza: interna',
};

const ERROR_LABELS: Record<string, string> = {
  invalid_body: 'Dados inválidos — confira os termos e a cor (#RRGGBB).',
  forbidden: 'Apenas administradores alteram o perfil da organização.',
};

function errorMessage(e: unknown): string {
  const err = e as Partial<ApiError> | null;
  const label = err?.error ? ERROR_LABELS[err.error] : undefined;
  if (label) return label;
  if (err?.status === 0) return 'Sem conexão com o servidor.';
  return err?.error ?? 'Erro inesperado.';
}

export function OrgProfileSection() {
  const profileQ = useQuery(orgProfileQuery());
  if (profileQ.isPending) return <PageSpinner />;
  if (profileQ.isError) {
    return (
      <p className="text-sm text-destructive">
        Erro ao carregar o perfil de operação: {errorMessage(profileQ.error)}
      </p>
    );
  }
  return <OrgProfileForm saved={profileQ.data} />;
}

function OrgProfileForm({ saved }: { saved: OrgProfile }) {
  const qc = useQueryClient();
  const [preset, setPreset] = useState<ProfilePreset>(saved.profile.preset);
  const [resourceSource, setResourceSource] = useState<ResourceSource | ''>(
    saved.profile.resource?.source ?? ''
  );
  const [terms, setTerms] = useState<Partial<Record<TermKey, string>>>(saved.profile.terms ?? {});
  const [primaryColor, setPrimaryColor] = useState(saved.profile.brand?.primaryColor ?? '');
  const [defaultLayout, setDefaultLayout] = useState<UiLayout>(saved.defaultLayout);
  const [done, setDone] = useState(false);

  const draft: TenantProfile = useMemo(() => {
    const cleanTerms = Object.fromEntries(
      Object.entries(terms).filter(([, v]) => v !== undefined && v.trim() !== '')
    ) as Partial<Record<TermKey, string>>;
    return {
      preset,
      ...(Object.keys(cleanTerms).length ? { terms: cleanTerms } : {}),
      ...(resourceSource ? { resource: { source: resourceSource } } : {}),
      ...(primaryColor.trim() ? { brand: { primaryColor: primaryColor.trim() } } : {}),
    };
  }, [preset, terms, resourceSource, primaryColor]);

  // o que o preset (sem sobrescritas) diz para cada termo: é o placeholder
  const presetTerms = useMemo(() => resolveTerms({ preset }), [preset]);
  // o que muda nas telas ao salvar, termo a termo
  const changes = useMemo(() => {
    const before = resolveTerms(saved.profile);
    const after = resolveTerms(draft);
    return TERM_KEYS.filter((k) => before[k] !== after[k]).map((k) => ({
      key: k,
      from: before[k],
      to: after[k],
    }));
  }, [saved.profile, draft]);

  const colorInvalid = primaryColor.trim() !== '' && !/^#[0-9a-fA-F]{6}$/.test(primaryColor.trim());

  const save = useMutation({
    mutationFn: async () => {
      const body: OrgProfilePutBody = { profile: draft, defaultLayout };
      const res = await api<OrgProfile>('/org/profile', { method: 'PUT', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(ORG_PROFILE_KEY, data);
      // o vocabulário e a marca de TODAS as telas vêm do /me
      void qc.invalidateQueries({ queryKey: ['erp', 'account', 'profile'] });
      setDone(true);
      setTimeout(() => setDone(false), 3000);
    },
  });

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        if (!colorInvalid) save.mutate();
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="profile-preset">Perfil de operação</Label>
          <Select
            id="profile-preset"
            value={preset}
            onChange={(e) => setPreset(e.target.value as ProfilePreset)}
          >
            {PROFILE_PRESETS.map((p) => (
              <option key={p} value={p}>
                {PROFILE_PRESET_LABELS[p]}
              </option>
            ))}
          </Select>
          <p className="text-xs text-muted-foreground">
            Define o vocabulário, o recurso principal e as naturezas. Trocar não apaga os termos
            sobrescritos abaixo.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="profile-resource">Recurso do atendimento</Label>
          <Select
            id="profile-resource"
            value={resourceSource}
            onChange={(e) => setResourceSource(e.target.value as ResourceSource | '')}
          >
            <option value="">Padrão do perfil</option>
            {RESOURCE_SOURCES.map((source) => (
              <option key={source} value={source}>
                {RESOURCE_SOURCE_LABELS[source]}
              </option>
            ))}
          </Select>
          <p className="text-xs text-muted-foreground">
            Sem recurso, a coluna e o filtro de recurso somem das telas.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="profile-color">Cor da marca</Label>
          <div className="flex items-center gap-2">
            <Input
              id="profile-color"
              value={primaryColor}
              onChange={(e) => setPrimaryColor(e.target.value)}
              placeholder="#0A8F7C"
              aria-invalid={colorInvalid}
              className="font-mono"
            />
            {primaryColor.trim() && !colorInvalid ? (
              <span
                aria-hidden
                className="size-8 shrink-0 rounded-md border"
                style={{ backgroundColor: primaryColor.trim() }}
              />
            ) : null}
          </div>
          <p className="text-xs text-muted-foreground">
            Destaque da marca no portal e nas páginas públicas. Vazio = cor do produto.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="profile-layout">Interface padrão</Label>
          <Select
            id="profile-layout"
            value={defaultLayout}
            onChange={(e) => setDefaultLayout(e.target.value as UiLayout)}
          >
            {UI_LAYOUTS.map((layout) => (
              <option
                key={layout}
                value={layout}
                // spec 018, FR-007: opt-in por pessoa até o piloto aprovar (o
                // valor já gravado continua selecionável, para não sumir da tela)
                disabled={
                  layout === 'simplified' &&
                  !SIMPLIFIED_LAYOUT_AS_TENANT_DEFAULT &&
                  saved.defaultLayout !== 'simplified'
                }
              >
                {UI_LAYOUT_LABELS[layout]}
                {layout === 'simplified' && !SIMPLIFIED_LAYOUT_AS_TENANT_DEFAULT
                  ? ' (por pessoa, até o fim do piloto)'
                  : ''}
              </option>
            ))}
          </Select>
          <p className="text-xs text-muted-foreground">
            Vale para quem não escolheu no menu do usuário.
          </p>
        </div>
      </div>

      <details className="rounded-md border p-3">
        <summary className="cursor-pointer text-sm font-medium">
          Termos ({Object.values(terms).filter((v) => v?.trim()).length} sobrescritos)
        </summary>
        <p className="mt-2 text-xs text-muted-foreground">
          Vazio = o termo do perfil (em cinza). Substantivos no meio da frase, em minúsculas
          ("cooperativa"); situações e naturezas como rótulo ("Aguardando cooperativa").
        </p>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {TERM_KEYS.map((key) => (
            <div key={key} className="space-y-1">
              <Label htmlFor={`term-${key}`} className="text-xs">
                {TERM_LABELS[key]}
              </Label>
              <Input
                id={`term-${key}`}
                value={terms[key] ?? ''}
                placeholder={presetTerms[key]}
                maxLength={60}
                onChange={(e) => setTerms((t) => ({ ...t, [key]: e.target.value }))}
                className="h-8 text-sm"
              />
            </div>
          ))}
        </div>
      </details>

      <div className="rounded-md border bg-muted/30 p-3">
        <p className="text-sm font-medium">O que muda nas telas ao salvar</p>
        {changes.length === 0 ? (
          <p className="mt-1 text-xs text-muted-foreground">Nenhum termo muda.</p>
        ) : (
          <ul className="mt-1 space-y-0.5 text-xs">
            {changes.map((c) => (
              <li key={c.key}>
                <span className="text-muted-foreground">{TERM_LABELS[c.key]}:</span> {c.from} →{' '}
                <span className="font-medium">{c.to}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex items-center gap-3">
        <Button type="submit" size="sm" disabled={save.isPending || colorInvalid}>
          {save.isPending ? 'Salvando…' : 'Salvar perfil'}
        </Button>
        {done ? (
          <span className="flex items-center gap-1 text-xs text-[#52C41A]">
            <CheckCircle2 className="size-3.5" />
            Perfil salvo.
          </span>
        ) : null}
        {save.error ? (
          <span className="text-xs text-destructive" role="alert">
            {errorMessage(save.error)}
          </span>
        ) : null}
      </div>
    </form>
  );
}
